import React, { useState } from 'react';
import { Card, CardBody, Col, FormGroup, Input, Label, Row, Button, Spinner } from 'reactstrap';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { getCustomersFindOne } from '../../../slices/customers/thunk';

interface DestinationSectionProps {
    values: {
        destinatarioIdType: string;
        destinatarioId: string;
        destinatarioName: string;
        arrivalAddress: string;
        route: string;
        reasonForTransfer: string;
        customsDoc: string;
    };
    touched: {
        destinatarioIdType?: boolean;
        destinatarioId?: boolean;
        destinatarioName?: boolean;
        arrivalAddress?: boolean;
        route?: boolean;
        reasonForTransfer?: boolean;
        customsDoc?: boolean;
    };
    errors: {
        destinatarioIdType?: string;
        destinatarioId?: string;
        destinatarioName?: string;
        arrivalAddress?: string;
        route?: string;
        reasonForTransfer?: string;
        customsDoc?: string;
    };
    onChange: (field: string, value: any) => void;
    formik: any;
    documentTypes?: any[];
}

const DestinationSection: React.FC<DestinationSectionProps> = ({
    values,
    touched,
    errors,
    onChange,
    formik,
    documentTypes = []
}) => {
    const dispatch = useDispatch<any>();
    const [isSearching, setIsSearching] = useState(false);
    const [suggestions, setSuggestions] = useState<any[]>([]);

    const handleIdChangeLocal = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        const selectedDocType = documentTypes.find((type: any) => type.id === formik.values.destinatarioIdType);
        if (selectedDocType) {
            const docName = selectedDocType.name.toUpperCase();
            if (docName.includes('CÉDULA') || docName.includes('RUC')) {
                if (!/^\d+$/.test(value)) return;
            }
        }

        if (!formik.values.destinatarioIdType) {
            formik.setFieldTouched('destinatarioIdType', true, false);
            formik.setFieldError('destinatarioIdType', 'Tipo de identificación es requerido');
        }

        onChange('destinatarioId', value);
        formik.handleChange(e);

        if (value === '') {
            clearCustomerFields();
        }

        validateIdentification(value);
    };

    const searchOnServer = async (identification: string) => {
        console.log("🔍 Iniciando búsqueda de cliente:", identification);
        console.log("📋 Errores actuales:", formik.errors.destinatarioId);
        
        if (!identification || formik.errors.destinatarioId) {
            console.log("❌ Cancelando búsqueda - identificación vacía o con errores");
            return;
        }

        setIsSearching(true);

        try {
            console.log("📡 Enviando petición a la API...");
            const result = await dispatch(getCustomersFindOne({
                identificationNumber: identification
                // Sin filtros de isCustomer/isCarrier para encontrar a cualquiera
            })).unwrap();
            
            console.log("📥 Respuesta de la API:", result);

            let foundCustomer = null;
            if (Array.isArray(result)) {
                foundCustomer = result.find((c: any) => c.identificationNumber === identification);
            } else if (result && typeof result === 'object') {
                if (result.identificationNumber === identification) {
                    foundCustomer = result;
                }
            }

            console.log("👤 Cliente encontrado:", foundCustomer);

            if (foundCustomer) {
                fillCustomerData(foundCustomer);
                toast.success("Cliente encontrado");
            } else {
                toast.info("Cliente no encontrado. Puede registrarlo.");
                clearCustomerFields();
            }
        } catch (error) {
            console.error("❌ Error buscando cliente:", error);
            toast.error("Error al buscar el cliente. Intente de nuevo.");
            clearCustomerFields();
        } finally {
            setIsSearching(false);
        }
    };

    const fillCustomerData = (customer: any) => {
        onChange('destinatarioName', customer.name || '');
        onChange('arrivalAddress', customer.address || '');
        
        // Usar directamente el tipo de identificación de la respuesta de la API
        if (customer.identityDocumentType && customer.identityDocumentType.id) {
            // Solo actualizar si el campo está vacío o si es diferente
            if (!formik.values.destinatarioIdType || formik.values.destinatarioIdType !== customer.identityDocumentType.id) {
                formik.setFieldValue('destinatarioIdType', customer.identityDocumentType.id);
            }
        }
    };

    const clearCustomerFields = () => {
        onChange('destinatarioName', '');
        onChange('arrivalAddress', '');
        formik.setFieldValue('destinatarioIdType', '');
        formik.setFieldTouched('destinatarioName', false);
        formik.setFieldError('destinatarioName', '');
        formik.setFieldTouched('arrivalAddress', false);
        formik.setFieldError('arrivalAddress', '');
        formik.setFieldTouched('destinatarioIdType', false);
        formik.setFieldError('destinatarioIdType', '');
    };

    const validateIdentification = (value: string) => {
        const selectedDocType = documentTypes.find((type: any) => type.id === formik.values.destinatarioIdType);
        if (selectedDocType) {
            const docName = selectedDocType.name.toUpperCase();
            
            if (docName.includes('RUC')) {
                if (!/^\d{13}$/.test(value)) {
                    formik.setFieldError('destinatarioId', 'El RUC debe tener 13 dígitos');
                } else {
                    formik.setFieldError('destinatarioId', '');
                }
            } else if (docName.includes('CÉDULA') || docName.includes('CEDULA')) {
                if (!/^\d{10}$/.test(value)) {
                    formik.setFieldError('destinatarioId', 'La cédula debe tener 10 dígitos');
                } else {
                    formik.setFieldError('destinatarioId', '');
                }
            }
        }
    };

    const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange(field, e.target.value);
    };

    return (
        <Card className="shadow border-0 h-100 overflow-hidden rounded-3">
            <div className="bg-soft-primary p-3 border-bottom border-light">
                <h5 className="card-title mb-0 text-primary fw-semibold d-flex align-items-center">
                    <i className="ri-map-pin-user-line me-2 fs-18"></i>
                    Información del Destinatario
                </h5>
            </div>
            <CardBody className="p-4 bg-white">
                <Row className="g-3">
                    <Col md={6}>
                        <FormGroup className="mb-0">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Tipo identificación <span className="text-danger">*</span>
                            </Label>
                            <Input 
                                type="select"
                                name="destinatarioIdType"
                                value={values.destinatarioIdType}
                                onChange={handleInputChange('destinatarioIdType')}
                                invalid={!!(touched.destinatarioIdType && errors.destinatarioIdType)}
                                className="form-select"
                            >
                                <option value="">Seleccionar...</option>
                                {documentTypes.map((type: any) => (
                                    <option key={type.id} value={type.id}>
                                        {type.name || type.description || type.code}
                                    </option>
                                ))}
                            </Input>
                            {touched.destinatarioIdType && errors.destinatarioIdType && (
                                <div className="invalid-feedback">{errors.destinatarioIdType}</div>
                            )}
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup className="mb-0 position-relative">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Identificación <span className="text-danger">*</span>
                            </Label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="ri-profile-line text-muted"></i>
                                </span>
                                <Input 
                                    name="destinatarioId"
                                    className="form-control border-start-0 ps-1"
                                    value={values.destinatarioId}
                                    onChange={handleIdChangeLocal}
                                    onKeyPress={(e) => {
                                        if (!/[0-9]/.test(e.key) && e.key !== 'Enter') {
                                            e.preventDefault();
                                        }
                                        if (e.key === 'Enter' && values.destinatarioId) {
                                            e.preventDefault();
                                            searchOnServer(values.destinatarioId);
                                        }
                                    }}
                                    onPaste={(e) => {
                                        e.preventDefault();
                                        const pastedText = e.clipboardData.getData('text');
                                        const numbersOnly = pastedText.replace(/[^0-9]/g, '');
                                        onChange('destinatarioId', numbersOnly);
                                    }}
                                    invalid={!!(touched.destinatarioId && errors.destinatarioId)}
                                    placeholder="CI / RUC"
                                    inputMode="numeric"
                                />
                                <Button 
                                    color="primary"
                                    className="btn-icon"
                                    onClick={() => values.destinatarioId && searchOnServer(values.destinatarioId)}
                                    disabled={isSearching || !values.destinatarioId}
                                    style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                                >
                                    {isSearching ? (
                                        <Spinner size="sm" />
                                    ) : (
                                        <i className="ri-search-line"></i>
                                    )}
                                </Button>
                                {touched.destinatarioId && errors.destinatarioId && (
                                    <div className="invalid-feedback">{errors.destinatarioId}</div>
                                )}
                            </div>
                            
                            {/* Sugerencias de autocompletado */}
                            {suggestions.length > 0 && (
                                <div className="position-absolute w-100 bg-white border rounded shadow-sm mt-1" 
                                     style={{ zIndex: 1000, top: '100%' }}>
                                    {suggestions.map((suggestion, index) => (
                                        <div 
                                            key={index}
                                            className="px-3 py-2 border-bottom cursor-pointer hover-bg-light"
                                            onClick={() => {
                                                fillCustomerData({
                                                    name: suggestion.name.split(' - ')[1],
                                                    address: 'Dirección del cliente'
                                                });
                                                setSuggestions([]);
                                            }}
                                        >
                                            <small className="text-muted">{suggestion.name}</small>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </FormGroup>
                    </Col>
                    <Col md={12}>
                        <FormGroup className="mb-0">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Razón social <span className="text-danger">*</span>
                            </Label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="ri-user-line text-muted"></i>
                                </span>
                                <Input 
                                    name="destinatarioName"
                                    className="form-control border-start-0 ps-1"
                                    value={values.destinatarioName}
                                    onChange={handleInputChange('destinatarioName')}
                                    invalid={!!(touched.destinatarioName && errors.destinatarioName)}
                                    placeholder="Nombre del cliente"
                                />
                                {touched.destinatarioName && errors.destinatarioName && (
                                    <div className="invalid-feedback">{errors.destinatarioName}</div>
                                )}
                            </div>
                        </FormGroup>
                    </Col>
                    <Col md={12}>
                        <FormGroup className="mb-0">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Dirección destino <span className="text-danger">*</span>
                            </Label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="ri-map-pin-line text-muted"></i>
                                </span>
                                <Input 
                                    name="arrivalAddress"
                                    className="form-control border-start-0 ps-1"
                                    value={values.arrivalAddress}
                                    onChange={handleInputChange('arrivalAddress')}
                                    invalid={!!(touched.arrivalAddress && errors.arrivalAddress)}
                                    placeholder="Dirección de entrega"
                                />
                                {touched.arrivalAddress && errors.arrivalAddress && (
                                    <div className="invalid-feedback">{errors.arrivalAddress}</div>
                                )}
                            </div>
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup className="mb-0">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Ruta
                            </Label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="ri-road-map-line text-muted"></i>
                                </span>
                                <Input 
                                    name="route"
                                    className="form-control border-start-0 ps-1"
                                    value={values.route}
                                    onChange={handleInputChange('route')}
                                    invalid={!!(touched.route && errors.route)}
                                    placeholder="Ej: Quito - Guayaquil"
                                />
                                {touched.route && errors.route && (
                                    <div className="invalid-feedback">{errors.route}</div>
                                )}
                            </div>
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup className="mb-0">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Motivo <span className="text-danger">*</span>
                            </Label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="ri-question-answer-line text-muted"></i>
                                </span>
                                <Input 
                                    name="reasonForTransfer"
                                    className="form-control border-start-0 ps-1"
                                    value={values.reasonForTransfer}
                                    onChange={handleInputChange('reasonForTransfer')}
                                    invalid={!!(touched.reasonForTransfer && errors.reasonForTransfer)}
                                    placeholder="Ej: Venta, Traslado"
                                />
                                {touched.reasonForTransfer && errors.reasonForTransfer && (
                                    <div className="invalid-feedback">{errors.reasonForTransfer}</div>
                                )}
                            </div>
                        </FormGroup>
                    </Col>
                    <Col md={12}>
                        <FormGroup className="mb-0">
                            <Label className="form-label text-muted fw-medium small text-uppercase">
                                Documento aduanero
                            </Label>
                            <div className="input-group">
                                <span className="input-group-text bg-light border-end-0">
                                    <i className="ri-file-paper-line text-muted"></i>
                                </span>
                                <Input 
                                    name="customsDoc"
                                    className="form-control border-start-0 ps-1"
                                    value={values.customsDoc}
                                    onChange={handleInputChange('customsDoc')}
                                    placeholder="Opcional"
                                />
                            </div>
                        </FormGroup>
                    </Col>
                </Row>
            </CardBody>
        </Card>
    );
};

export default DestinationSection;
